import { useEffect, useRef, useState } from "react";
import { Button, Form, Input, Select } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { localizeDigits, t, useStoreLanguage } from "../../../features/store-language/i18n.ts";
import AppFeedback from "../../shared/AppFeedback.tsx";
import { getTenantInfo } from "../storefrontService.ts";
import CustomerAuthShell from "./CustomerAuthShell.tsx";
import { agencyContext } from "./agencyIdentityService.ts";
import { normalizeLoginDigits } from "./customerIdentityService.ts";
import { agencyRegistrationPasswordRules, normalizeAgencyPassword } from "./agencyPasswordPolicy.ts";
import { registerAgency, registrationOptions, type RegistrationOptions, type RegistrationValues } from "./agencyRegistrationService.ts";
import "./CustomerLogin.css";
import "./AgencyRegistrationPage.css";

const stepFields: (keyof RegistrationValues)[][] = [
  ["firstName", "lastName", "mobile"],
  ["provinceId", "cityId", "address", "description"],
  ["languageCode", "password", "passwordConfirmation"],
];
const agencyLanguages = [
  { value: "fa", label: "فارسی" },
  { value: "en", label: "English" },
  { value: "ar", label: "العربية" },
];

export default function AgencyRegistrationPage() {
  const language = useStoreLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [draftId] = useState(function () { return crypto.randomUUID(); });
  const [furthestStep, setFurthestStep] = useState(0);
  const requestedStep = location.state?.registrationStep;
  const step = location.state?.registrationDraftId === draftId && Number.isInteger(requestedStep)
    ? Math.max(0, Math.min(requestedStep, furthestStep, 2))
    : 0;
  const activeStep = useRef<HTMLLIElement>(null);
  const previousStep = useRef(step);
  const [validating, setValidating] = useState(false);
  const validationLocked = useRef(false);
  const [form] = Form.useForm<RegistrationValues>();
  const [companyInfo, setCompanyInfo] = useState<Awaited<ReturnType<typeof getTenantInfo>> | null>(null);
  const [options, setOptions] = useState<RegistrationOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const locked = useRef(false);
  const mobile = Form.useWatch("mobile", form);
  const provinceId = Form.useWatch("provinceId", form);
  const cities = options?.provinces.find(function (province) { return province.id === provinceId; })?.cities ?? [];

  useEffect(function () {
    let current = true;
    getTenantInfo({ anonymous: true, notifyErrors: false }).then(function (info) {
      if (current) setCompanyInfo(info);
    }).catch(function () { /* Branding is optional; registration availability is checked separately. */ });
    return function () { current = false; };
  }, [language]);

  useEffect(function () {
    let current = true;
    setLoading(true);
    setFailed(false);
    registrationOptions().then(function (result) {
      if (current) setOptions(result);
    }).catch(function (error) {
      if (current) {
        setFailed(true);
        AppFeedback.httpError(error);
      }
    }).finally(function () { if (current) setLoading(false); });
    return function () { current = false; };
  }, [attempt]);

  useEffect(function () {
    if (previousStep.current !== step) {
      activeStep.current?.focus({ preventScroll: true });
      activeStep.current?.closest(".customer-auth-shell")?.scrollTo({ top: 0 });
      previousStep.current = step;
    }
  }, [step]);

  function goToStep(nextStep: number) {
    setFurthestStep(function (value) { return Math.max(value, nextStep); });
    navigate(location.pathname + location.search, {
      state: { ...location.state, registrationStep: nextStep, registrationDraftId: draftId },
    });
  }

  async function continueRegistration() {
    if (locked.current || validationLocked.current) return;
    validationLocked.current = true;
    setValidating(true);
    try {
      await form.validateFields(step === 2 ? undefined : stepFields[step]);
      if (step < 2) goToStep(step + 1);
      else await submit(form.getFieldsValue(true));
    } catch (error) {
      const failure = error as { errorFields?: { name: string[] }[] };
      const firstField = failure.errorFields?.[0]?.name?.[0] as keyof RegistrationValues | undefined;
      if (firstField) {
        const invalidStep = stepFields.findIndex(function (fields) { return fields.includes(firstField); });
        if (invalidStep >= 0 && invalidStep !== step) goToStep(invalidStep);
        requestAnimationFrame(function () {
          form.scrollToField(firstField, { block: "center" });
          form.getFieldInstance(firstField)?.focus?.();
        });
        AppFeedback.error({ title: t("اطلاعات مشخص‌شده را بررسی کنید.") });
      } else if (error instanceof Error) {
        AppFeedback.error({ title: t("ادامه ثبت‌نام ممکن نشد. دوباره تلاش کنید.") });
      }
    } finally {
      validationLocked.current = false;
      setValidating(false);
    }
  }

  async function submit(values: RegistrationValues) {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    try {
      await registerAgency({
        ...values,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        mobile: normalizeLoginDigits(values.mobile).trim(),
        password: normalizeAgencyPassword(values.password),
        passwordConfirmation: normalizeAgencyPassword(values.passwordConfirmation),
      });
      // The cookie is already committed. Never invite a duplicate registration if context refresh fails.
      try { await agencyContext(); } catch { /* The session boundary will refresh context at the destination. */ }
      navigate("/products", { replace: true });
    } catch (error) {
      AppFeedback.httpError(error);
    } finally {
      locked.current = false;
      setBusy(false);
    }
  }

  const enabled = !loading && !failed && options?.allowSelfRegistration === true;
  const stepTitles = [t("مشخصات مدیریت"), t("اطلاعات نمایندگی"), t("رمز ورود")];
  const required = [{ required: true, whitespace: true, message: t("این فیلد را تکمیل کنید.") }];
  return (
    <CustomerAuthShell
      companyInfo={companyInfo}
      title={t("ثبت‌نام نماینده فروش")}
      description=""
      variant={enabled ? "register" : "invitation-status"}
      showLanguageSelector
      footer={(
        <div className="customer-auth-register-prompt">
          <span>{t("حساب کاربری دارید؟")}</span>
          <Link
            className="customer-auth-register-link"
            to={`/login?language=${language}`}
          >
            {t("ورود")}
          </Link>
        </div>
      )}
    >
      {loading ? (
        <p role="status">{t("در حال دریافت اطلاعات…")}</p>
      ) : failed ? (
        <div className="agency-registration-state">
          <p>{t("اطلاعات ثبت‌نام دریافت نشد. دوباره تلاش کنید.")}</p>
          <Button
            block
            onClick={function () { setAttempt(function (value) { return value + 1; }); }}
          >
            {t("تلاش دوباره")}
          </Button>
        </div>
      ) : !enabled ? (
        <p role="status">{t("ثبت‌نام بدون دعوت برای این مجموعه فعال نیست. با دعوت‌نامه وارد شوید.")}</p>
      ) : (
        <Form
          form={form}
          component="div"
          initialValues={{ languageCode: ["fa", "en", "ar"].includes(language) ? language : "fa" }}
          layout="vertical"
          className="customer-auth-form agency-registration-form"
          disabled={busy}
          requiredMark={false}
        >
          <form
            onSubmit={function (event) {
              event.preventDefault();
              event.stopPropagation();
              void continueRegistration();
            }}
            aria-busy={busy}
          >
            <ol
              className="agency-registration-progress"
              aria-label={t("مراحل ثبت‌نام")}
            >
              {stepTitles.map(function (title, index) {
                return (
                  <li
                    key={index}
                    ref={index === step ? activeStep : undefined}
                    tabIndex={-1}
                    aria-current={index === step ? "step" : undefined}
                    className={index <= step ? "is-reached" : undefined}
                  >
                    <span
                      className="agency-registration-step-number"
                      aria-hidden="true"
                    >
                      {localizeDigits(index + 1)}
                    </span>
                    <span>{title}</span>
                  </li>
                );
              })}
            </ol>
            <section
              hidden={step !== 0}
              aria-label={stepTitles[0]}
            >
              <div className="agency-registration-fields">
                <Form.Item
                  name="firstName"
                  label={t("نام")}
                  rules={required}
                >
                  <Input
                    autoComplete="given-name"
                    maxLength={99}
                  />
                </Form.Item>
                <Form.Item
                  name="lastName"
                  label={t("نام خانوادگی")}
                  rules={required}
                >
                  <Input
                    autoComplete="family-name"
                    maxLength={100}
                  />
                </Form.Item>
              </div>
              <Form.Item
                name="mobile"
                label={t("شماره موبایل")}
                normalize={normalizeLoginDigits}
                extra={t("برای ورودهای بعدی از این شماره و رمز عبور استفاده کنید.")}
                rules={[{ required: true, pattern: /^09[0-9]{9}$/, message: t("شماره موبایل ایران را با ۰۹ و ۱۱ رقم وارد کنید.") }]}
              >
                <Input
                  dir="ltr"
                  type="tel"
                  inputMode="tel"
                  autoComplete="username"
                  maxLength={11}
                  placeholder="09xxxxxxxxx"
                />
              </Form.Item>
            </section>
            <section
              hidden={step !== 1}
              aria-label={stepTitles[1]}
            >
              <div className="agency-registration-fields agency-registration-location-fields">
                <Form.Item
                  name="provinceId"
                  label={t("استان")}
                  rules={[{ required: true, message: t("استان را انتخاب کنید.") }]}
                >
                  <Select
                    size="large"
                    popupClassName="agency-registration-menu"
                    showSearch
                    optionFilterProp="label"
                    placeholder={t("انتخاب استان")}
                    options={options?.provinces.map(function (province) { return { value: province.id, label: province.name }; })}
                    onChange={function () { form.setFieldValue("cityId", undefined); }}
                  />
                </Form.Item>
                <Form.Item
                  name="cityId"
                  label={t("شهر")}
                  rules={[{ required: true, message: t("شهر را انتخاب کنید.") }]}
                >
                  <Select
                    size="large"
                    popupClassName="agency-registration-menu"
                    showSearch
                    optionFilterProp="label"
                    disabled={busy || !provinceId}
                    placeholder={provinceId ? t("انتخاب شهر") : t("ابتدا استان را انتخاب کنید")}
                    options={cities.map(function (city) { return { value: city.id, label: city.name }; })}
                  />
                </Form.Item>
              </div>
              <Form.Item
                name="address"
                label={t("آدرس محل فعالیت")}
                rules={required}
              >
                <Input.TextArea
                  autoComplete="street-address"
                  rows={3}
                  maxLength={1000}
                />
              </Form.Item>
              <Form.Item
                name="description"
                label={t("توضیحات (اختیاری)")}
              >
                <Input.TextArea
                  rows={4}
                  placeholder={t("اگر نکته‌ای درباره فعالیت نمایندگی دارید، اینجا بنویسید.")}
                  maxLength={2000}
                />
              </Form.Item>
            </section>
            <section
              hidden={step !== 2}
              aria-label={stepTitles[2]}
            >
              <div className="agency-registration-review">
                <div>
                  <span>{t("شماره ورود")}</span>
                  <bdi
                    dir="ltr"
                    data-portal-digits="literal"
                  >
                    {mobile}
                  </bdi>
                  <Button
                    type="link"
                    disabled={busy}
                    onClick={function () { goToStep(0); }}
                  >
                    {t("ویرایش مشخصات")}
                  </Button>
                </div>
              </div>
              <Form.Item
                name="languageCode"
                label={t("زبان نمایندگی")}
                rules={[{ required: true, message: t("زبان نمایندگی را انتخاب کنید.") }]}
              >
                <Select
                  size="large"
                  popupClassName="agency-registration-menu"
                  options={agencyLanguages}
                  optionRender={function (option) {
                    return (
                      <span
                        lang={String(option.value)}
                        dir={option.value === "en" ? "ltr" : "rtl"}
                      >
                        {option.label}
                      </span>
                    );
                  }}
                />
              </Form.Item>
              <Form.Item
                name="password"
                label={t("رمز عبور")}
                rules={agencyRegistrationPasswordRules()}
                extra={t("رمز عبور باید حداقل ۸ کاراکتر باشد.")}
              >
                <Input.Password
                  autoComplete="new-password"
                  maxLength={256}
                />
              </Form.Item>
              <Form.Item
                name="passwordConfirmation"
                label={t("تکرار رمز عبور")}
                dependencies={["password"]}
                rules={[{
                  validator: async function (_, value) {
                    if (!value || normalizeAgencyPassword(value) !== normalizeAgencyPassword(form.getFieldValue("password"))) {
                      throw new Error(t("تکرار رمز عبور یکسان نیست."));
                    }
                  },
                }]}
              >
                <Input.Password
                  autoComplete="new-password"
                  maxLength={256}
                />
              </Form.Item>
            </section>
            <div className="customer-auth-submit agency-registration-actions">
              {step > 0 ? (
                <Button
                  htmlType="button"
                  disabled={busy || validating}
                  onClick={function () { goToStep(step - 1); }}
                >
                  {t("مرحله قبل")}
                </Button>
              ) : null}
              <Button
                type="primary"
                htmlType="submit"
                onClick={function (event) {
                  event.preventDefault();
                  void continueRegistration();
                }}
                disabled={busy || validating}
                loading={busy || validating}
              >
                {step === 0 ? t("ادامه: اطلاعات نمایندگی") : step === 1 ? t("ادامه: رمز ورود") : t("ثبت‌نام و ورود")}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </CustomerAuthShell>
  );
}
